import type { Json } from '@hive/shared';
import type { GridView } from './GridToolbar';
import { NO_FILTERS, type DueFilter, type GridFilters, type SortKey } from './model';

// Grid's saved views: the view type, filters and sort, stored as JSON per
// member (saved_views). Pure, so it's unit-tested without Supabase.

export interface GridViewConfig {
  view: GridView;
  filters: GridFilters;
  sort: SortKey;
}

const VIEWS: readonly GridView[] = ['list', 'board', 'gantt'];
const SORTS: readonly SortKey[] = ['manual', 'due', 'priority', 'newest', 'title'];
const DUES: readonly DueFilter[] = ['any', 'overdue', 'today', 'week', 'none'];

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** A saved config read defensively: anything missing or unknown falls back to the defaults. */
export function parseViewConfig(json: Json): GridViewConfig {
  const c = (json && typeof json === 'object' && !Array.isArray(json) ? json : {}) as Record<string, unknown>;
  const f = (c.filters && typeof c.filters === 'object' ? c.filters : {}) as Record<string, unknown>;
  const priority = typeof f.priority === 'number' && f.priority >= 1 && f.priority <= 4 ? f.priority : null;
  return {
    view: VIEWS.includes(c.view as GridView) ? (c.view as GridView) : 'list',
    sort: SORTS.includes(c.sort as SortKey) ? (c.sort as SortKey) : 'manual',
    filters: {
      assignee: str(f.assignee),
      labelId: str(f.labelId),
      priority,
      due: DUES.includes(f.due as DueFilter) ? (f.due as DueFilter) : 'any',
      text: typeof f.text === 'string' ? f.text : '',
      sprint: str(f.sprint),
    },
  };
}

export function toJson(config: GridViewConfig): Json {
  return { view: config.view, sort: config.sort, filters: { ...config.filters } };
}

export function sameConfig(a: GridViewConfig, b: GridViewConfig): boolean {
  return JSON.stringify(toJson(a)) === JSON.stringify(toJson(b));
}

/**
 * A saved config for one game: a label from another game, or a sprint that
 * isn't this game's, is dropped ("current" and "backlog" work in any game).
 */
export function configForGame(
  config: GridViewConfig,
  labelIds: ReadonlySet<string>,
  sprintIds: ReadonlySet<string>,
): GridViewConfig {
  const { labelId, sprint } = config.filters;
  return {
    ...config,
    filters: {
      ...config.filters,
      labelId: labelId && labelIds.has(labelId) ? labelId : null,
      sprint: sprint && (sprint === 'current' || sprint === 'backlog' || sprintIds.has(sprint)) ? sprint : null,
    },
  };
}

export const DEFAULT_CONFIG: GridViewConfig = { view: 'list', filters: NO_FILTERS, sort: 'manual' };
